import type { VipProfile } from '../types';
import { mockVipReviews } from './mockVipReviews';

const u = (id: string, w = 1000) =>
  `https://images.unsplash.com/photo-${id}?w=${w}&q=80&auto=format&fit=crop&crop=faces`;

/**
 * MOCK roster — 12 fictional editorial profiles.
 * Mood/bio lines are deliberately suggestive but not explicit.
 * No graphic content. No bookings hinted at within HOTTR.
 *
 * Providers advertise rates on their profile (classifieds-style, like
 * Tryst / Rentmen). HOTTR still does not mediate the transaction —
 * contact + payment all happen off-platform. See
 * docs/HOTTR_VIP_PRODUCT_RULES.md for the legal posture.
 *
 * Photos are hot-linked Unsplash IDs already verified to resolve in the
 * existing hottr.app build (see src/data/mockProfiles.ts for the canonical set).
 *
 * Rates use Melbourne-market figures (AUD), stored in cents to avoid
 * floating-point drift.
 */
const baseProfiles: Omit<VipProfile, 'reviews'>[] = [
  {
    id: 'vip_01',
    memberNo: 14,
    handle: 'alexei',
    displayName: 'ALEXEI',
    type: 'companion',
    city: 'Melbourne',
    suburb: 'Carlton',
    visibility: ['men', 'women'],
    availability: 'available_tonight',
    verification: 'verified',
    contactMethods: ['signal', 'telegram', 'hottr_intro'],
    mood: 'late dinner, low light',
    bio: 'A long, slow evening. Conversation first. Old hotel bars, single malts, the kind of room where you don\'t check your phone.',
    screening: 'Brief intro. References welcome but not required.',
    travelRadiusKm: 25,
    newThisWeek: false,
    discreet: true,
    scene: 'flash',
    sceneSeed: 17,
    photos: [
      u('1488161628813-04466f872be2'),
      u('1539571696357-5a69c17a67c6'),
      u('1531891437562-4301cf35b7e4'),
    ],
    joinedAt: '2025-11-04',
    rates: { hour1: 45000, hour2: 80000, overnight: 280000, currency: 'AUD' },
  },
  {
    id: 'vip_02',
    memberNo: 23,
    handle: 'roman',
    displayName: 'ROMAN',
    type: 'companion',
    city: 'Melbourne',
    suburb: 'Fitzroy',
    visibility: ['men'],
    availability: 'available_this_week',
    verification: 'verified',
    contactMethods: ['signal', 'whatsapp'],
    mood: 'warehouse rooftop, after eleven',
    bio: 'Architect by day. Better company than that line suggests. Says less. Reads more.',
    screening: 'A short intro is enough. Tell me what tonight is.',
    travelRadiusKm: 15,
    newThisWeek: false,
    discreet: false,
    scene: 'club_room',
    sceneSeed: 41,
    photos: [
      u('1500648767791-00dcc994a43e'),
      u('1506794778202-cad84cf45f1d'),
    ],
    joinedAt: '2025-09-12',
    rates: { hour1: 50000, hour2: 90000, overnight: 320000, currency: 'AUD' },
  },
  {
    id: 'vip_03',
    memberNo: 7,
    handle: 'phoenix',
    displayName: 'PHOENIX',
    type: 'companion',
    city: 'Melbourne',
    suburb: 'St Kilda',
    visibility: ['women', 'trans'],
    availability: 'travelling',
    verification: 'verified',
    contactMethods: ['signal', 'telegram', 'whatsapp', 'hottr_intro'],
    mood: 'between cities, weekend only',
    bio: 'Touring lighting designer. Home for one weekend a month. Selective about what that weekend looks like.',
    screening: 'Photo intro and a sentence on what you like.',
    travelRadiusKm: 50,
    newThisWeek: false,
    discreet: true,
    scene: 'haze',
    sceneSeed: 19,
    photos: [u('1564564321837-a57b7070ac4f'), u('1542178243-bc20204b769f')],
    joinedAt: '2025-08-08',
    rates: { hour1: 60000, hour2: 100000, overnight: 350000, currency: 'AUD' },
  },
  {
    id: 'vip_04',
    memberNo: 41,
    handle: 'sebastian',
    displayName: 'SEBASTIAN',
    type: 'masseur',
    city: 'Melbourne',
    suburb: 'South Yarra',
    visibility: ['men', 'women', 'trans'],
    availability: 'available_tonight',
    verification: 'verified',
    contactMethods: ['signal', 'telegram'],
    mood: 'hands quiet, room quieter',
    bio: 'Trained massage therapist. Studio near the river. Long table. Long evenings.',
    screening: 'A brief description of what you want from the hour.',
    travelRadiusKm: 0,
    newThisWeek: true,
    discreet: true,
    scene: 'mirror',
    sceneSeed: 9,
    photos: [u('1568602471122-7832951cc4c5')],
    joinedAt: '2026-04-28',
    rates: {
      hour1: 30000,
      hour2: 50000,
      overnight: 150000,
      currency: 'AUD',
      notes: 'Studio bookings only. Overnight by arrangement.',
    },
  },
  {
    id: 'vip_05',
    memberNo: 33,
    handle: 'mario',
    displayName: 'MARIO',
    type: 'companion',
    city: 'Sydney',
    suburb: 'Surry Hills',
    visibility: ['men'],
    availability: 'available_this_week',
    verification: 'pending',
    contactMethods: ['telegram', 'hottr_intro'],
    mood: 'eastern suburbs, ride along',
    bio: 'Born in Naples. Lives a few blocks from the harbour. The longer you talk, the better it goes.',
    screening: 'Reference from anyone on the roster gets you straight in.',
    travelRadiusKm: 20,
    newThisWeek: false,
    discreet: false,
    scene: 'alley_neon',
    sceneSeed: 33,
    photos: [u('1531891437562-4301cf35b7e4'), u('1495195129352-aeb325a55b65')],
    joinedAt: '2026-02-14',
    rates: { hour1: 35000, hour2: 60000, overnight: 220000, currency: 'AUD' },
  },
  {
    id: 'vip_06',
    memberNo: 56,
    handle: 'iris',
    displayName: 'IRIS',
    type: 'companion',
    city: 'Melbourne',
    suburb: 'Collingwood',
    visibility: ['women', 'men'],
    availability: 'unavailable',
    verification: 'verified',
    contactMethods: ['hottr_intro'],
    mood: 'on a deadline. quiet week.',
    bio: 'Fashion editor. Selective. Rare on the roster. Worth the wait.',
    screening: 'HOTTR intro only this week.',
    travelRadiusKm: 10,
    newThisWeek: false,
    discreet: true,
    scene: 'club_room',
    sceneSeed: 17,
    photos: [u('1534528741775-53994a69daeb'), u('1492562080023-ab3db95bfbce')],
    joinedAt: '2025-06-21',
    rates: {
      hour1: 70000,
      hour2: 130000,
      overnight: 400000,
      currency: 'AUD',
      notes: 'Selective. References quietly preferred.',
    },
  },
  {
    id: 'vip_07',
    memberNo: 12,
    handle: 'nico',
    displayName: 'NICO',
    type: 'companion',
    city: 'Melbourne',
    suburb: 'Richmond',
    visibility: ['men', 'trans'],
    availability: 'available_tonight',
    verification: 'verified',
    contactMethods: ['signal', 'whatsapp'],
    mood: 'standing drink. staying late.',
    bio: 'Old hotel rooms, new wine bars, anywhere with a banquette and a phone-free policy.',
    screening: 'Send a sentence. I\'ll send back a time and a place.',
    travelRadiusKm: 30,
    newThisWeek: false,
    discreet: false,
    scene: 'flash',
    sceneSeed: 11,
    photos: [u('1519085360753-af0119f7cbe7')],
    joinedAt: '2025-12-02',
    rates: { hour1: 40000, hour2: 70000, overnight: 260000, currency: 'AUD' },
  },
  {
    id: 'vip_08',
    memberNo: 28,
    handle: 'camille',
    displayName: 'CAMILLE',
    type: 'companion',
    city: 'Melbourne',
    suburb: 'Brunswick East',
    visibility: ['men', 'women', 'trans'],
    availability: 'available_this_week',
    verification: 'verified',
    contactMethods: ['signal', 'telegram', 'hottr_intro'],
    mood: 'vinyl, espresso martinis',
    bio: 'DJ residencies twice a month. The other nights are yours, if you ask early.',
    screening: 'A quick photo. Voice note preferred.',
    travelRadiusKm: 25,
    newThisWeek: true,
    discreet: false,
    scene: 'haze',
    sceneSeed: 23,
    photos: [u('1583454110551-21f2fa2afe61'), u('1472099645785-5658abf4ff4e')],
    joinedAt: '2026-04-30',
    rates: { hour1: 45000, hour2: 80000, overnight: 280000, currency: 'AUD' },
  },
  {
    id: 'vip_09',
    memberNo: 67,
    handle: 'kai',
    displayName: 'KAI',
    type: 'masseur',
    city: 'Melbourne',
    suburb: 'CBD',
    visibility: ['men'],
    availability: 'available_tonight',
    verification: 'verified',
    contactMethods: ['signal', 'whatsapp'],
    mood: 'studio bookings only',
    bio: 'Sports massage background. Strong, slow, deliberate. CBD studio with the lift code.',
    screening: 'Tell me what hurts and how long you have.',
    travelRadiusKm: 0,
    newThisWeek: false,
    discreet: true,
    scene: 'backstage',
    sceneSeed: 51,
    photos: [u('1488161628813-04466f872be2')],
    joinedAt: '2025-10-15',
    rates: {
      hour1: 35000,
      hour2: 60000,
      overnight: 150000,
      currency: 'AUD',
      notes: 'Studio only. Overnight rare; ask first.',
    },
  },
  {
    id: 'vip_10',
    memberNo: 19,
    handle: 'ash',
    displayName: 'ASH',
    type: 'companion',
    city: 'Melbourne',
    suburb: 'Windsor',
    visibility: ['women'],
    availability: 'travelling',
    verification: 'pending',
    contactMethods: ['telegram', 'hottr_intro'],
    mood: 'in town fridays only',
    bio: 'Frequent flyer. In Melbourne every other weekend. Calendar fills up earlier than you\'d think.',
    screening: 'A line about your week is enough.',
    travelRadiusKm: 35,
    newThisWeek: false,
    discreet: true,
    scene: 'mirror',
    sceneSeed: 14,
    photos: [u('1552642986-ccb41e7059e7')],
    joinedAt: '2025-07-03',
    rates: { hour1: 38000, hour2: 65000, overnight: 220000, currency: 'AUD' },
  },
  {
    id: 'vip_11',
    memberNo: 44,
    handle: 'lev_ari',
    displayName: 'LEV & ARI',
    type: 'duo',
    city: 'Melbourne',
    suburb: 'Prahran',
    visibility: ['men', 'women'],
    availability: 'available_this_week',
    verification: 'verified',
    contactMethods: ['signal', 'telegram'],
    mood: 'two for the long table',
    bio: 'Working duo. Together a year. Both verified. Long evenings, no rush.',
    screening: 'A short intro and a phone number we can call.',
    travelRadiusKm: 20,
    newThisWeek: false,
    discreet: false,
    scene: 'club_room',
    sceneSeed: 37,
    photos: [
      u('1500648767791-00dcc994a43e'),
      u('1506794778202-cad84cf45f1d'),
      u('1519085360753-af0119f7cbe7'),
    ],
    joinedAt: '2025-11-19',
    rates: {
      hour1: 70000,
      hour2: 130000,
      overnight: 420000,
      currency: 'AUD',
      notes: 'Duo rate. Both attending. No solo bookings.',
    },
  },
  {
    id: 'vip_12',
    memberNo: 5,
    handle: 'juliette',
    displayName: 'JULIETTE',
    type: 'companion',
    city: 'Melbourne',
    suburb: 'Toorak',
    visibility: ['men', 'women', 'trans'],
    availability: 'available_tonight',
    verification: 'verified',
    contactMethods: ['signal', 'hottr_intro'],
    mood: 'opera, then somewhere darker',
    bio: 'Long evenings. Old hotel. Newer ideas. Conversations that earn the second hour.',
    screening: 'A few sentences. References quietly preferred.',
    travelRadiusKm: 15,
    newThisWeek: false,
    discreet: true,
    scene: 'flash',
    sceneSeed: 5,
    photos: [u('1542178243-bc20204b769f'), u('1564564321837-a57b7070ac4f')],
    joinedAt: '2025-05-10',
    rates: { hour1: 60000, hour2: 110000, overnight: 380000, currency: 'AUD' },
  },
];

export const mockVipProfiles: VipProfile[] = baseProfiles.map((p) => ({
  ...p,
  reviews: mockVipReviews[p.id] ?? [],
}));

export const findVipProfile = (handle: string): VipProfile | undefined =>
  mockVipProfiles.find((p) => p.handle === handle);
